import { followUpKey, toSelectedCodes, type ReopenEnqueteFormValues } from "./schema";
import { type Answer, type DefaultValues, type Question } from "./types";

const knownOptionCodes = (question: Question, answer: Answer): string[] => {
  const codes = question.options.map((option) => option.optionCode);
  return (answer.optionCodes ?? []).filter((code) => codes.includes(code));
};

// サーバから受け取った前回入力を react-hook-form の初期値に変換する
export const answersToFormValues = (
  defaultValues: DefaultValues,
  questions: Question[],
): ReopenEnqueteFormValues => {
  const values: ReopenEnqueteFormValues = {};

  questions.forEach((question) => {
    values[question.questionCode] = question.questionType === "multiple_choice" ? [] : "";
    if (question.options.some((option) => option.requiresFollowUpText)) {
      values[followUpKey(question.questionCode)] = "";
    }
  });

  const questionByCode = new Map(questions.map((question) => [question.questionCode, question]));

  defaultValues.answers.forEach((answer) => {
    const question = questionByCode.get(answer.questionCode);
    if (!question) return;

    const optionCodes = knownOptionCodes(question, answer);
    if (optionCodes.length > 0) {
      values[question.questionCode] =
        question.questionType === "single_choice" ? optionCodes[0] : optionCodes;
    }

    if (
      typeof answer.followUpText === "string" &&
      question.options.some((option) => option.requiresFollowUpText)
    ) {
      values[followUpKey(question.questionCode)] = answer.followUpText;
    }
  });

  return values;
};

// react-hook-form のフォーム値を enquete_answers へ送る回答の配列に整える
// 並び順（＝送信 hidden の連番 index）はマスタ順
export const toSubmitAnswers = (
  questions: Question[],
  values: ReopenEnqueteFormValues,
): Answer[] => {
  const included = new Map<string, Answer>();

  questions.forEach((question) => {
    // 親の該当選択肢が選ばれていない子設問（orphan）は送らない
    if (question.parentQuestionCode !== null) {
      const parent = included.get(question.parentQuestionCode);
      if (!question.parentOptionCode || !parent?.optionCodes?.includes(question.parentOptionCode)) {
        return;
      }
    }

    const selected = new Set(toSelectedCodes(values[question.questionCode]));
    const optionCodes = question.options
      .map((option) => option.optionCode)
      .filter((code) => selected.has(code));
    if (optionCodes.length === 0) return;

    const requiresFollowUpText = question.options.some(
      (option) => option.requiresFollowUpText && optionCodes.includes(option.optionCode),
    );
    const followUpText = values[followUpKey(question.questionCode)];
    included.set(question.questionCode, {
      questionCode: question.questionCode,
      optionCodes,
      ...(requiresFollowUpText && typeof followUpText === "string" && followUpText !== ""
        ? { followUpText }
        : {}),
    });
  });

  return Array.from(included.values());
};
